import { InputText, InputTextProps } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { ChangeEvent } from 'react';
import {
  FieldValues,
  useController,
  UseControllerProps,
  UseFormClearErrors,
} from 'react-hook-form';

import ErrorMessage from '../../error-message';
import styles from './TextInput.module.css';

export type TextInputProps<T extends FieldValues> = Omit<InputTextProps, 'name'> & {
  label?: string;
  clearErrors?: UseFormClearErrors<T>;
  showError?: boolean;
  customIcon?: React.ReactNode;
  customIconPosition?: 'left' | 'right';
} & UseControllerProps<T>;

const TextInput = <T extends FieldValues>({
  name,
  control,
  rules,
  defaultValue,
  label,
  className,
  clearErrors,
  showError = true,
  customIcon,
  customIconPosition = 'right',
  autoComplete = 'off',
  ...props
}: TextInputProps<T>) => {
  const {
    field: { ref, onChange, ...inputProps },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event);
    clearErrors?.(name);
  };

  return (
    <div className='field p-fluid'>
      <span
        className={classNames(
          `p-input-icon-${customIconPosition}`,
          'p-float-label',
          props.disabled && styles['disabled-input-field'],
        )}
      >
        <InputText
          id={name}
          ref={ref}
          onChange={handleOnChange}
          {...props}
          {...inputProps}
          className={classNames(className, {
            'p-invalid': error,
          })}
          autoComplete={autoComplete}
        />
        {customIcon && <>{customIcon}</>}
        {label && <label htmlFor={name}>{label}</label>}
      </span>
      {showError && invalid && (
        <div className={classNames(styles['error-message-box'])}>
          <ErrorMessage message={error?.message || ''} />
        </div>
      )}
    </div>
  );
};

export default TextInput;
